import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Trash2, 
  Edit, 
  ChevronDown, 
  ChevronRight, 
  Package,
  Search,
  X,
  Upload,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { 
  getAdminCategories, 
  createCategory,
  updateCategory,
  deleteCategory,
  addBrand,
  updateBrand,
  deleteBrand,
  addProduct,
  updateProduct,
  deleteProduct,
  uploadFile
} from '../../services/api';

const AdminProducts = () => {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedBrands, setExpandedBrands] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedBrandId, setSelectedBrandId] = useState(null);

  // Form states
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', image: '' });
  const [brandForm, setBrandForm] = useState({ name: '', logo: '' });
  const [productForm, setProductForm] = useState({ name: '', sizes: '', image: '' });
  const [editingBrand, setEditingBrand] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const data = await getAdminCategories();
      setCategories(data);
    } catch (error) {
      toast.error('Failed to fetch categories');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const toggleBrand = (brandId) => {
    setExpandedBrands(prev => ({
      ...prev,
      [brandId]: !prev[brandId]
    }));
  };

  // Category operations
  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, categoryForm);
        toast.success('Category updated');
      } else {
        await createCategory(categoryForm);
        toast.success('Category created');
      }
      setShowCategoryModal(false);
      setCategoryForm({ name: '', description: '', image: '' });
      setEditingCategory(null);
      fetchCategories();
    } catch (error) {
      toast.error('Failed to save category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category? This will delete all brands and products within it.')) return;
    try {
      await deleteCategory(categoryId);
      toast.success('Category deleted');
      fetchCategories();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  // Brand operations
  const handleSaveBrand = async () => {
    try {
      const brandData = { 
        name: brandForm.name,
        logo: brandForm.logo || null
      };
      
      if (editingBrand) {
        await updateBrand(selectedCategoryId, editingBrand.id, brandData);
        toast.success('Brand updated');
      } else {
        await addBrand(selectedCategoryId, brandData);
        toast.success('Brand added');
      }
      setShowBrandModal(false);
      setBrandForm({ name: '', logo: '' });
      setEditingBrand(null);
      setSelectedCategoryId(null);
      fetchCategories();
    } catch (error) {
      toast.error('Failed to save brand');
    }
  };

  const handleDeleteBrand = async (categoryId, brandId) => {
    if (!window.confirm('Are you sure you want to delete this brand? This will delete all products within it.')) return;
    try {
      await deleteBrand(categoryId, brandId);
      toast.success('Brand deleted');
      fetchCategories();
    } catch (error) {
      toast.error('Failed to delete brand');
    }
  };

  // Product operations
  const handleSaveProduct = async () => {
    try {
      const sizesArray = productForm.sizes.split(',').map(s => s.trim()).filter(Boolean);
      const productData = { 
        name: productForm.name, 
        sizes: sizesArray,
        image: productForm.image || null
      };
      
      if (editingProduct) {
        await updateProduct(selectedCategoryId, selectedBrandId, editingProduct.id, productData);
        toast.success('Product updated');
      } else {
        await addProduct(selectedCategoryId, selectedBrandId, productData);
        toast.success('Product added');
      }
      setShowProductModal(false);
      setProductForm({ name: '', sizes: '', image: '' });
      setEditingProduct(null);
      setSelectedCategoryId(null);
      setSelectedBrandId(null);
      fetchCategories();
    } catch (error) {
      toast.error('Failed to save product');
    }
  };

  const handleDeleteProduct = async (categoryId, brandId, productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await deleteProduct(categoryId, brandId, productId);
      toast.success('Product deleted');
      fetchCategories();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  // File upload handlers
  const handleBrandLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsUploading(true);
    try {
      const url = await uploadFile(file);
      setBrandForm(prev => ({ ...prev, logo: url }));
      toast.success('Logo uploaded');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCategoryImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsUploading(true);
    try {
      const url = await uploadFile(file);
      setCategoryForm(prev => ({ ...prev, image: url }));
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleProductImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsUploading(true);
    try {
      const url = await uploadFile(file);
      setProductForm(prev => ({ ...prev, image: url }));
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  // Filter categories based on search
  const filteredCategories = categories.filter(category => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    if (category.name.toLowerCase().includes(query)) return true;
    return category.brands?.some(brand => 
      brand.name.toLowerCase().includes(query) ||
      brand.products?.some(product => product.name.toLowerCase().includes(query))
    );
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[var(--primary-teal)] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-products">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: 'var(--primary-teal)' }}>
            Products
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-medium)' }}>
            Manage categories, brands, and products
          </p>
        </div>
        <Button 
          onClick={() => setShowCategoryModal(true)}
          className="btn-gold gap-2"
          data-testid="add-category-btn"
        >
          <Plus size={18} />
          Add Category
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2" size={18} style={{ color: 'var(--text-light)' }} />
        <Input
          placeholder="Search categories, brands, or products..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="product-search"
        />
      </div>

      {/* Categories List */}
      <div className="space-y-4">
        {filteredCategories.length === 0 ? (
          <Card className="border-0 shadow-md">
            <CardContent className="py-12 text-center">
              <Package size={48} className="mx-auto mb-4" style={{ color: 'var(--text-light)' }} />
              <p style={{ color: 'var(--text-medium)' }}>No categories found</p>
            </CardContent>
          </Card>
        ) : (
          filteredCategories.map((category) => (
            <Card key={category.id} className="border-0 shadow-md overflow-hidden">
              <div 
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => toggleCategory(category.id)}
              >
                <div className="flex items-center gap-3">
                  {expandedCategories[category.id] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  <div>
                    <h3 className="font-semibold" style={{ color: 'var(--primary-teal)' }}>
                      {category.name}
                    </h3>
                    <p className="text-xs" style={{ color: 'var(--text-medium)' }}>
                      {category.brands?.length || 0} brands
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedCategoryId(category.id);
                      setShowBrandModal(true);
                    }}
                    className="gap-1"
                  >
                    <Plus size={14} /> Brand
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditingCategory(category);
                      setCategoryForm({
                        name: category.name,
                        description: category.description,
                        image: category.image
                      });
                      setShowCategoryModal(true);
                    }}
                  >
                    <Edit size={16} />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDeleteCategory(category.id)}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>

              {/* Brands */}
              {expandedCategories[category.id] && (
                <div className="border-t bg-gray-50">
                  {category.brands?.length === 0 ? (
                    <p className="p-4 text-sm text-center" style={{ color: 'var(--text-medium)' }}>
                      No brands in this category
                    </p>
                  ) : (
                    category.brands?.map((brand) => (
                      <div key={brand.id} className="border-b last:border-b-0">
                        <div 
                          className="flex items-center justify-between p-3 pl-8 cursor-pointer hover:bg-gray-100"
                          onClick={() => toggleBrand(brand.id)}
                        >
                          <div className="flex items-center gap-2">
                            {expandedBrands[brand.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            {brand.logo && (
                              <img src={brand.logo} alt={brand.name} className="h-6 w-6 object-contain rounded" />
                            )}
                            <span className="font-medium text-sm">{brand.name}</span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200">
                              {brand.products?.length || 0} products
                            </span>
                          </div>
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedCategoryId(category.id);
                                setSelectedBrandId(brand.id);
                                setShowProductModal(true);
                              }}
                              className="h-7 px-2 text-xs"
                            >
                              <Plus size={12} className="mr-1" /> Product
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2"
                              onClick={() => {
                                setSelectedCategoryId(category.id);
                                setEditingBrand(brand);
                                setBrandForm({
                                  name: brand.name,
                                  logo: brand.logo || ''
                                });
                                setShowBrandModal(true);
                              }}
                            >
                              <Edit size={14} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteBrand(category.id, brand.id)}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>

                        {/* Products */}
                        {expandedBrands[brand.id] && (
                          <div className="bg-white pl-12 py-2">
                            {brand.products?.length === 0 ? (
                              <p className="text-xs py-2" style={{ color: 'var(--text-medium)' }}>
                                No products
                              </p>
                            ) : (
                              <div className="space-y-1">
                                {brand.products?.map((product) => (
                                  <div 
                                    key={product.id} 
                                    className="flex items-center justify-between p-2 rounded hover:bg-gray-50 text-sm"
                                  >
                                    <div>
                                      <span className="font-medium">{product.name}</span>
                                      <span className="text-xs ml-2" style={{ color: 'var(--text-light)' }}>
                                        {product.sizes?.join(', ')}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 px-2"
                                        onClick={() => {
                                          setSelectedCategoryId(category.id);
                                          setSelectedBrandId(brand.id);
                                          setEditingProduct(product);
                                          setProductForm({
                                            name: product.name,
                                            sizes: product.sizes?.join(', ') || '',
                                            image: product.image || ''
                                          });
                                          setShowProductModal(true);
                                        }}
                                      >
                                        <Edit size={12} />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 px-2 text-red-600 hover:bg-red-50"
                                        onClick={() => handleDeleteProduct(category.id, brand.id, product.id)}
                                      >
                                        <Trash2 size={12} />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </Card>
          ))
        )}
      </div>

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <Card className="w-full max-w-md border-0 shadow-xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ color: 'var(--primary-teal)' }}>
                {editingCategory ? 'Edit Category' : 'Add Category'}
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  setShowCategoryModal(false);
                  setEditingCategory(null);
                  setCategoryForm({ name: '', description: '', image: '' });
                }}
              >
                <X size={18} />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <Input
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Category name"
                  data-testid="category-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Textarea
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Category description"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Category Image</label>
                <div className="space-y-2">
                  {/* File Upload */}
                  <div className="flex items-center gap-2">
                    <label 
                      className={`flex items-center gap-2 px-4 py-2 rounded-md cursor-pointer transition-colors ${
                        isUploading ? 'bg-gray-100 text-gray-400' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      {isUploading ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Upload size={16} />
                      )}
                      <span className="text-sm">{isUploading ? 'Uploading...' : 'Upload from Computer'}</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleCategoryImageUpload}
                        disabled={isUploading}
                        className="hidden"
                      />
                    </label>
                  </div>
                  {/* URL Input */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">or paste URL:</span>
                  </div>
                  <Input
                    value={categoryForm.image}
                    onChange={(e) => setCategoryForm(prev => ({ ...prev, image: e.target.value }))}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                {categoryForm.image && (
                  <div className="mt-2 rounded-lg overflow-hidden border h-24 w-32">
                    <img 
                      src={categoryForm.image} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                      onError={(e) => e.target.parentElement.style.display = 'none'}
                    />
                  </div>
                )}
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCategoryModal(false);
                    setEditingCategory(null);
                    setCategoryForm({ name: '', description: '', image: '' });
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveCategory}
                  disabled={!categoryForm.name}
                  className="btn-gold"
                  data-testid="save-category-btn"
                >
                  {editingCategory ? 'Update' : 'Create'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Brand Modal */}
      {showBrandModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <Card className="w-full max-w-md border-0 shadow-xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ color: 'var(--primary-teal)' }}>
                {editingBrand ? 'Edit Brand' : 'Add Brand'}
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  setShowBrandModal(false);
                  setBrandForm({ name: '', logo: '' });
                  setEditingBrand(null);
                  setSelectedCategoryId(null);
                }}
              >
                <X size={18} />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Brand Name *</label>
                <Input
                  value={brandForm.name}
                  onChange={(e) => setBrandForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Brand name"
                  data-testid="brand-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Brand Logo</label>
                <div className="space-y-2">
                  {/* File Upload */}
                  <div className="flex items-center gap-2">
                    <label 
                      className={`flex items-center gap-2 px-4 py-2 rounded-md cursor-pointer transition-colors ${
                        isUploading ? 'bg-gray-100 text-gray-400' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      {isUploading ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Upload size={16} />
                      )}
                      <span className="text-sm">{isUploading ? 'Uploading...' : 'Upload from Computer'}</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleBrandLogoUpload}
                        disabled={isUploading}
                        className="hidden"
                      />
                    </label>
                  </div>
                  {/* URL Input */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">or paste URL:</span>
                  </div>
                  <Input
                    value={brandForm.logo}
                    onChange={(e) => setBrandForm(prev => ({ ...prev, logo: e.target.value }))}
                    placeholder="https://example.com/logo.jpg"
                    data-testid="brand-logo-input"
                  />
                </div>
                {brandForm.logo && (
                  <div className="mt-2 rounded-lg overflow-hidden border h-16 w-16 bg-white p-1">
                    <img 
                      src={brandForm.logo} 
                      alt="Preview" 
                      className="w-full h-full object-contain"
                      onError={(e) => e.target.parentElement.style.display = 'none'}
                    />
                  </div>
                )}
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowBrandModal(false);
                    setBrandForm({ name: '', logo: '' });
                    setEditingBrand(null);
                    setSelectedCategoryId(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveBrand}
                  disabled={!brandForm.name}
                  className="btn-gold"
                  data-testid="save-brand-btn"
                >
                  {editingBrand ? 'Update' : 'Add Brand'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Product Modal */}
      {showProductModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <Card className="w-full max-w-md border-0 shadow-xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ color: 'var(--primary-teal)' }}>
                {editingProduct ? 'Edit Product' : 'Add Product'}
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  setShowProductModal(false);
                  setProductForm({ name: '', sizes: '', image: '' });
                  setEditingProduct(null);
                  setSelectedCategoryId(null);
                  setSelectedBrandId(null);
                }}
              >
                <X size={18} />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Product Name *</label>
                <Input
                  value={productForm.name}
                  onChange={(e) => setProductForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Product name"
                  data-testid="product-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Sizes (comma-separated)</label>
                <Input
                  value={productForm.sizes}
                  onChange={(e) => setProductForm(prev => ({ ...prev, sizes: e.target.value }))}
                  placeholder="e.g., 100g, 250g, 500g"
                  data-testid="product-sizes-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Product Image</label>
                <div className="space-y-2">
                  {/* File Upload */}
                  <div className="flex items-center gap-2">
                    <label 
                      className={`flex items-center gap-2 px-4 py-2 rounded-md cursor-pointer transition-colors ${
                        isUploading ? 'bg-gray-100 text-gray-400' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      {isUploading ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Upload size={16} />
                      )}
                      <span className="text-sm">{isUploading ? 'Uploading...' : 'Upload from Computer'}</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleProductImageUpload}
                        disabled={isUploading}
                        className="hidden"
                      />
                    </label>
                  </div>
                  {/* URL Input */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">or paste URL:</span>
                  </div>
                  <Input
                    value={productForm.image}
                    onChange={(e) => setProductForm(prev => ({ ...prev, image: e.target.value }))}
                    placeholder="https://example.com/image.jpg"
                    data-testid="product-image-input"
                  />
                </div>
                {productForm.image && (
                  <div className="mt-2 rounded-lg overflow-hidden border h-24 w-24">
                    <img 
                      src={productForm.image} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowProductModal(false);
                    setProductForm({ name: '', sizes: '', image: '' });
                    setEditingProduct(null);
                    setSelectedCategoryId(null);
                    setSelectedBrandId(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveProduct}
                  disabled={!productForm.name}
                  className="btn-gold"
                  data-testid="save-product-btn"
                >
                  {editingProduct ? 'Update' : 'Add Product'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
